import { useState } from 'react';
import {
  Stack, Button, Chip, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, IconButton, Tooltip, Typography,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { amCmsConnectionsApi, type CmsConnection, type CmsConnectionInput } from '../../api/amCms.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const PROVIDERS = [
  { value: 'mock', label: 'Mock (demo)' },
  { value: 'generic', label: 'Generic REST (BBPS/vendor)' },
];

export function CmsConnectionsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('am_cms.manage');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [editing, setEditing] = useState<CmsConnection | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<CmsConnection | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['cms-connections', page, limit], queryFn: () => amCmsConnectionsApi.list({ page, limit }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['cms-connections'] });
  const deleteMut = useMutation({ mutationFn: (id: number) => amCmsConnectionsApi.remove(id), onSuccess: () => { setDeleting(null); setToast('Connection deleted'); invalidate(); }, onError: (e) => setToast(extractApiError(e)) });

  const columns: Column<CmsConnection>[] = [
    { key: 'name', label: 'Name' },
    { key: 'provider', label: 'Provider', render: (r) => <Chip size="small" label={r.provider} /> },
    { key: 'baseUrl', label: 'Base URL', render: (r) => r.baseUrl ?? '—' },
    { key: 'auth', label: 'Auth', render: (r) => `${r.authType}${r.hasApiKey ? ` · key ${r.apiKeyMasked}` : ' · no key'}` },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={r.status === 'active' ? 'success' : 'default'} label={r.status} /> },
    { key: 'actions', label: '', width: 90, render: (r) => canManage && (
      <Stack direction="row">
        <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>
      </Stack>
    ) },
  ];

  return (
    <>
      <PageHeader title="CMS Connections" subtitle="Connect each OEM/CPO's Charging Management System. API keys are encrypted; map sites to a connection on the Charger CMS page."
        action={canManage ? <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setOpen(true); }}>New Connection</Button> : undefined} />
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id} />

      {open && <ConnectionDialog editing={editing} onClose={() => setOpen(false)} onDone={() => { setOpen(false); setToast('Saved'); invalidate(); }} />}
      <ConfirmDialog open={Boolean(deleting)} title="Delete CMS connection" message={`Delete "${deleting?.name}"? Sites using it will be unmapped.`} confirmLabel="Delete"
        onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function ConnectionDialog({ editing, onClose, onDone }: { editing: CmsConnection | null; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState<CmsConnectionInput>({
    name: editing?.name ?? '', provider: editing?.provider ?? 'mock', baseUrl: editing?.baseUrl ?? '',
    authType: editing?.authType ?? 'bearer', authHeader: editing?.authHeader ?? '', apiKey: '', status: editing?.status ?? 'active',
  });
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => {
      const body: CmsConnectionInput = { ...form, apiKey: form.apiKey || undefined };
      return editing ? amCmsConnectionsApi.update(editing.id, body) : amCmsConnectionsApi.create(body);
    },
    onSuccess: onDone, onError: (e) => setError(extractApiError(e)),
  });
  const isGeneric = form.provider !== 'mock';

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>{editing ? 'Edit CMS Connection' : 'New CMS Connection'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth helperText="e.g. ChargeZone — North CPO" />
          <TextField select label="Provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} fullWidth>
            {PROVIDERS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </TextField>
          {isGeneric && (
            <>
              <TextField label="Base URL" value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} fullWidth placeholder="https://api.yourcms.com/v1" />
              <Stack direction="row" spacing={2}>
                <TextField select label="Auth type" value={form.authType} onChange={(e) => setForm({ ...form, authType: e.target.value as CmsConnectionInput['authType'] })} sx={{ minWidth: 150 }}>
                  <MenuItem value="bearer">Bearer token</MenuItem>
                  <MenuItem value="api_key">API key header</MenuItem>
                  <MenuItem value="basic">Basic</MenuItem>
                </TextField>
                {form.authType === 'api_key' && <TextField label="Header name" value={form.authHeader} onChange={(e) => setForm({ ...form, authHeader: e.target.value })} fullWidth placeholder="x-api-key" />}
              </Stack>
              <TextField label="API key" type="password" value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value })} fullWidth
                helperText={editing ? 'Leave blank to keep the existing key' : 'Stored encrypted; never shown again'} />
            </>
          )}
          <TextField select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'inactive' })} fullWidth>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          {form.provider === 'mock' && <Typography variant="caption" color="text.secondary">Mock provider returns demo data (no real CMS calls) — good for trying the flow.</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={form.name.trim().length < 2 || mut.isPending} onClick={() => mut.mutate()}>{editing ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
